// ============================================================================
// CONTENT CONFIGURATION - MAIN EXPORT
// ============================================================================

/**
 * This is the main entry point for accessing website content.
 * Import from this file in your components.
 * 
 * Usage:
 * import { siteContent } from '@/config/content'
 * 
 * Or import specific sections:
 * import { heroContent, servicesContent } from '@/config'
 */

export { siteContent } from './content';
export type { 
  SiteContent,
  NavigationItem,
  Service,
  Testimonial,
  ProcessStep,
  Stat,
  CaseStudy,
  BlogPost,
  Career,
} from './content';

// ============================================================================
// CONVENIENCE EXPORTS - Individual Sections
// ============================================================================
// You can also import individual sections for convenience

import { siteContent } from './content';

export const siteInfo = siteContent.site;
export const navigationContent = siteContent.navigation;
export const heroContent = siteContent.hero;
export const aboutContent = siteContent.about;
export const servicesContent = siteContent.services;
export const statsContent = siteContent.stats;
export const processContent = siteContent.process;
export const testimonialsContent = siteContent.testimonials;
export const contactContent = siteContent.contact;
export const footerContent = siteContent.footer;
export const ctaContent = siteContent.cta;
export const whyChooseUsContent = siteContent.whyChooseUs;
export const partnersContent = siteContent.partners;
export const caseStudiesContent = siteContent.caseStudies;
export const blogContent = siteContent.blog;
export const careersContent = siteContent.careers;
export const articlesContent = siteContent.articles;
export const experienceContent = siteContent.experience;
export const topbarContent = siteContent.topbar;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get navigation menu items
 */
export const getNavigationItems = () => siteContent.navigation.menuItems;

/**
 * Get all services
 */
export const getServices = () => siteContent.services.serviceList;

/**
 * Get a specific service by ID
 */
export const getServiceById = (id: number) => 
  siteContent.services.serviceList.find(service => service.id === id);

/**
 * Get all testimonials
 */
export const getTestimonials = () => siteContent.testimonials.reviews;

/**
 * Get all blog posts
 */
export const getBlogPosts = () => siteContent.blog.posts;

/**
 * Get contact information
 */
export const getContactInfo = () => siteContent.contact.info;

/**
 * Get footer sections
 */
export const getFooterSections = () => siteContent.footer.sections;

/**
 * Get social media links
 */
export const getSocialLinks = () => siteContent.footer.social;

/**
 * Get company information
 */
export const getCompanyInfo = () => ({
  name: siteContent.site.name,
  tagline: siteContent.site.tagline,
  description: siteContent.site.description,
  phone: siteContent.contact.info.phone.value,
  email: siteContent.contact.info.email.value,
  address: siteContent.contact.info.address.value,
});

/**
 * Get all statistics
 */
export const getStats = () => siteContent.stats.items;

/**
 * Get process steps
 */
export const getProcessSteps = () => siteContent.process.steps;

/**
 * Get open career positions
 */
export const getOpenPositions = () => siteContent.careers.openPositions;

/**
 * Get case studies
 */
export const getCaseStudies = () => siteContent.caseStudies.studies;
